import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowRight, ArrowUp } from "lucide-react";
import clsx from "clsx";
import { formatMetres } from "../../formatMetres";
import { elevation } from "./elevation";
import type { HeightLeg } from "../../heightProfile";
import type { HeightProfileProps } from "./types";

const WIDTH = 300;
const HEIGHT = 96;
const LEVEL_KEY = {
  floor: "heightLevel_floorM",
  controls: "heightLevel_controlsM",
  luminaire: "heightLevel_luminaireM",
} as const;

export const HeightProfile = ({ profile, boardName }: HeightProfileProps) => {
  const { t, i18n } = useTranslation();

  const metres = (value: number) => formatMetres({ value, language: i18n.language });
  const height = (value: number) =>
    `${formatMetres({ value, language: i18n.language, fractionDigits: 2 })} m`;
  const line = (key: string, params?: Record<string, string>) =>
    t(`smartSurveyor.cablesBeta.${key}`, params);
  const trayName = (trayType: string) => trayType || line("heightTrayUntyped");

  const describe = (leg: HeightLeg): string => {
    if (leg.reason === "board")
      return line("heightLegBoard", {
        board: boardName,
        from: height(leg.fromM),
        target: leg.trayType ? trayName(leg.trayType) : line("heightLevel_ceilingVoidM"),
        to: height(leg.toM),
      });
    if (leg.reason === "trayChange")
      return line("heightLegTrayChange", { tray: trayName(leg.trayType), to: height(leg.toM) });
    if (leg.reason === "device")
      return line("heightLegDevice", { level: line(LEVEL_KEY[profile.level]), to: height(leg.toM) });
    if (leg.reason === "passDevice" && leg.level)
      return line("heightLegPassDevice", { level: line(LEVEL_KEY[leg.level]), to: height(leg.toM) });
    if (leg.reason === "boardEnd") return line("heightLegBoardEnd", { to: height(leg.toM) });
    if (leg.reason === "leaveDevice") return line("heightLegLeaveDevice", { to: height(leg.toM) });
    if (leg.reason === "tray") return line("heightLegAlongTray", { tray: trayName(leg.trayType) });
    return line("heightLegFreeRun", { at: height(leg.toM) });
  };

  const sketch = elevation({ profile, width: WIDTH, height: HEIGHT });
  const path = sketch.points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");
  const start = sketch.points[0];
  const end = sketch.points[sketch.points.length - 1];

  return (
    <div
      data-testid={`height-profile-${profile.destinationId}`}
      className="mb-1 ml-1 mt-1 rounded-md border border-amber-200 bg-amber-50/60 px-2 py-2"
    >
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-auto w-full"
        role="img"
        aria-label={line("heightProfileTitle")}
      >
        <line x1={0} x2={WIDTH} y1={sketch.floorY} y2={sketch.floorY} stroke="#94a3b8" strokeWidth={1.5} />
        {sketch.levels.map((level) => (
          <g key={level.heightM}>
            <line
              x1={30}
              x2={WIDTH}
              y1={level.y}
              y2={level.y}
              stroke="#cbd5e1"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <text x={0} y={level.y + 3.5} fontSize={10} fill="#475569">
              {formatMetres({ value: level.heightM, language: i18n.language, fractionDigits: 2 })}
            </text>
          </g>
        ))}
        <path d={path} fill="none" stroke="#475569" strokeWidth={2} strokeLinejoin="round" />
        {sketch.points.map((point, index) =>
          point.isVertical ? (
            <line
              key={index}
              x1={point.x}
              x2={point.x}
              y1={sketch.points[index - 1].y}
              y2={point.y}
              stroke="#2563eb"
              strokeWidth={3}
            />
          ) : null,
        )}
        <rect x={start.x - 4} y={start.y - 4} width={8} height={8} fill="#1e293b" />
        <circle cx={end.x} cy={end.y} r={4} fill="#d97706" />
      </svg>

      <ul className="mt-1.5 space-y-1">
        {profile.legs.map((leg, index) => {
          const Icon = leg.kind === "rise" ? ArrowUp : leg.kind === "drop" ? ArrowDown : ArrowRight;
          return (
            <li key={index} className="flex items-start gap-1.5 text-[13px] leading-snug text-slate-700">
              <Icon
                size={14}
                className={clsx("mt-0.5 flex-none", leg.kind === "run" ? "text-slate-500" : "text-blue-600")}
              />
              <span className="w-14 flex-none text-right font-semibold tabular-nums">
                {metres(leg.lengthM)} m
              </span>
              <span className="min-w-0 flex-1">{describe(leg)}</span>
            </li>
          );
        })}
      </ul>
      <div className="mt-1.5 border-t border-amber-200 pt-1 text-[13px] text-slate-700">
        {line("heightProfileSum", {
          vertical: metres(profile.verticalM),
          horizontal: metres(profile.horizontalM),
        })}
      </div>
    </div>
  );
};
